import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, RefreshCw, ShieldAlert, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles" }) : "—");
const GROUP_TONE = { live: "bg-emerald-50 text-emerald-800", orphaned: "bg-zinc-100 text-zinc-700", completed: "bg-sky-50 text-sky-800", archived: "bg-amber-50 text-amber-800", paused: "bg-violet-50 text-violet-800" };

function CloseDialog({ job, onClose, onDone }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/campaign-send-jobs/${job.send_job_id}/close`);
            toast.success(`Job closed: ${r.data.queued_skipped} queued texts skipped, none sent${r.data.in_flight_left ? `; ${r.data.in_flight_left} mid-send left alone` : ""}`);
            onDone(r.data);
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="close-job-dialog">
                <DialogHeader>
                    <DialogTitle>Close this send job?</DialogTitle>
                    <DialogDescription>
                        {job.campaign_name || "Deleted campaign"}: {job.queued_sms} queued SMS and {job.queued_email} queued emails will be
                        marked skipped (job_closed) and never sent. {job.running} mid-send left alone. This cannot be undone.
                    </DialogDescription>
                    {job.group === "paused" && job.is_active_job && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="close-job-paused-warning">
                            This is the current run of a paused campaign. Closing it ends that run; the campaign will need Start for a new run.
                        </div>
                    )}
                </DialogHeader>
                <div className="font-mono text-xs text-muted-foreground" data-testid="close-job-id">Job {job.send_job_id}</div>
                <Input placeholder="Type CLOSE to confirm" value={text} onChange={(e) => setText(e.target.value)} data-testid="close-job-confirm-input" />
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="close-job-cancel">Cancel</Button>
                    <Button variant="destructive" disabled={text !== "CLOSE" || busy} onClick={submit} data-testid="close-job-submit">
                        {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Close job
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function StaleSendJobs() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [target, setTarget] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        try { setData((await api.get("/campaign-send-jobs/stale-report")).data); }
        catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    }, []);
    const isAdmin = user?.role === "admin";
    useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
    if (user && user.role !== "admin") return <Navigate to="/" replace />;

    return (
        <div className="space-y-6" data-testid="stale-send-jobs-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Stale send jobs</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Send jobs still marked open. Closing one marks its queued texts skipped (job_closed) and never sends them.
                        Jobs of running campaigns are live and cannot be closed.
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="stale-jobs-refresh">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}<span className="ml-1">Refresh</span>
                </Button>
            </div>
            {data && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stale-jobs-totals">
                    {[["Open jobs", data.jobs.length, "total-open"], ["Closable", data.closable, "total-closable"],
                      ["SMS that would skip", data.sms_would_skip, "total-sms"], ["Emails that would skip", data.email_would_skip, "total-email"],
                      ["Other", data.other_would_skip, "total-other"]].map(([l, v, id]) => (
                        <div key={id} className="rounded-lg border bg-white px-4 py-3">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</div>
                            <div className="text-xl font-semibold tabular-nums" data-testid={`stale-jobs-${id}`}>{v.toLocaleString()}</div>
                        </div>
                    ))}
                </div>
            )}
            {data && (
                <div className="rounded-xl border bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid="stale-jobs-table">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                                {["Group", "Campaign", "Job ID", "Created (PT)", "Last activity (PT)", "Queued SMS", "Queued email", "Mid-send", "Sent", ""].map((h) => <th key={h} className="py-2 px-3 text-left">{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {data.jobs.map((j) => (
                                <tr key={j.send_job_id} className="border-b last:border-0" data-testid={`stale-job-row-${j.send_job_id}`}>
                                    <td className="py-2 px-3"><span className={`rounded px-2 py-0.5 text-xs font-medium ${GROUP_TONE[j.group] || "bg-zinc-100"}`} data-testid={`stale-job-group-${j.send_job_id}`}>{j.group}</span></td>
                                    <td className="py-2 px-3">{j.campaign_name || <span className="text-muted-foreground">Deleted campaign</span>}</td>
                                    <td className="py-2 px-3 font-mono text-xs">{j.send_job_id}</td>
                                    <td className="py-2 px-3 text-xs">{fmt(j.created_at)}</td>
                                    <td className="py-2 px-3 text-xs">{fmt(j.last_activity)}</td>
                                    <td className="py-2 px-3 tabular-nums" data-testid={`stale-job-sms-${j.send_job_id}`}>{j.queued_sms}</td>
                                    <td className="py-2 px-3 tabular-nums" data-testid={`stale-job-email-${j.send_job_id}`}>{j.queued_email}</td>
                                    <td className="py-2 px-3 tabular-nums">{j.running}</td>
                                    <td className="py-2 px-3 tabular-nums">{j.accepted}</td>
                                    <td className="py-2 px-3 text-right">
                                        {j.can_close
                                            ? <Button size="sm" variant="outline" onClick={() => setTarget(j)} data-testid={`stale-job-close-${j.send_job_id}`}><XCircle className="h-4 w-4 mr-1" />Close</Button>
                                            : <span className="inline-flex items-center gap-1 text-xs text-emerald-800" data-testid={`stale-job-live-${j.send_job_id}`}><ShieldAlert className="h-3.5 w-3.5" />Live, protected</span>}
                                    </td>
                                </tr>
                            ))}
                            {data.jobs.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-muted-foreground" data-testid="stale-jobs-empty">No open send jobs.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
            {target && <CloseDialog job={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />}
        </div>
    );
}
